import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { X, Users, Sparkles, Trophy, Mic, BookOpen, Check, Loader2 } from 'lucide-react';
import { useEntitlements, usePaywallState, closePaywall } from '@hooks/useEntitlements';
import authHub from '@/services/authHub';
import revenueCat, { PRODUCTS } from '@/services/revenueCat';

/**
 * PaywallModal — the upgrade prompt.
 *
 * On native (iOS/Android): shows the three Host Pass packages fetched from
 * RevenueCat and drives the in-app purchase flow.
 *
 * On web: falls back to the "download the app" message (web subscriptions
 * will be added via Stripe in a later phase).
 *
 * Listens to the paywall state via usePaywallState() — any component can
 * trigger it by calling openPaywall() from useEntitlements.
 */
export default function PaywallModal() {
  const { open, trigger } = usePaywallState();
  const { isSignedIn, signIn } = useEntitlements();
  const isNative = Capacitor.isNativePlatform();

  const [packages, setPackages] = useState([]);
  const [selectedTier, setSelectedTier] = useState('ANNUAL');
  const [purchasing, setPurchasing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  // Fetch offerings when the modal opens on native
  useEffect(() => {
    if (!open || !isNative || !isSignedIn) return;
    let cancelled = false;

    (async () => {
      // Ensure offerings are loaded (initialise() already fetches, but refresh
      // in case the modal opens before init completed)
      await revenueCat.fetchOfferings();
      if (cancelled) return;
      const pkgs = revenueCat.getHostPassPackages();
      setPackages(pkgs);
    })();

    return () => { cancelled = true; };
  }, [open, isNative, isSignedIn]);

  if (!open) return null;

  const handleClose = () => {
    if (purchasing) return; // don't let the user close mid-purchase
    setErrorMessage(null);
    closePaywall();
  };

  const handleWebUpgrade = () => {
    closePaywall();
    if (!isSignedIn) {
      signIn();
    } else {
      authHub.redirectToUpgrade();
    }
  };

  const handleNativePurchase = async () => {
    setErrorMessage(null);
    const pkg = revenueCat.getPackageByTier(selectedTier.toLowerCase());
    if (!pkg) {
      setErrorMessage('That option isn\'t available right now. Please try again.');
      return;
    }
    setPurchasing(true);
    const result = await revenueCat.purchasePackage(pkg);
    setPurchasing(false);

    if (result.success) {
      // Refresh auth hub claims so the new entitlement flows through —
      // the webhook will have fired, but we force a token refresh to pick
      // it up without waiting for the normal refresh interval.
      try { await authHub.refreshClaims?.(); } catch { /* ignore */ }
      closePaywall();
      return;
    }

    if (result.cancelled) return; // silent — user backed out
    setErrorMessage(result.error || 'Purchase failed. Please try again.');
  };

  const handleRestore = async () => {
    setErrorMessage(null);
    setRestoring(true);
    const result = await revenueCat.restorePurchases();
    setRestoring(false);

    if (result.success && result.hasHostPass) {
      try { await authHub.refreshClaims?.(); } catch { /* ignore */ }
      closePaywall();
      return;
    }

    if (result.success) {
      setErrorMessage('No previous Host Pass purchase found on this account.');
    } else {
      setErrorMessage(result.error || 'Unable to restore purchases.');
    }
  };

  // Customise the headline based on what triggered the paywall
  const headlines = {
    'fourth-player': {
      title: 'Make room for more friends',
      subtitle: 'Your workshop is full at 3 apprentices. Host Pass lets you invite up to 8.',
    },
    'premium-game-mode': {
      title: 'This mode needs Host Pass',
      subtitle: 'Challenge, Relay and Broken Telephone are part of the full workshop curriculum.',
    },
    'voice-chat': {
      title: 'Voice chat is a Host Pass feature',
      subtitle: 'The workshop banter — what medieval studios always had.',
    },
    'account-menu': {
      title: 'Become a DrawLab Host',
      subtitle: 'Open bigger workshops, unlock every game mode, and become the one who brings people together.',
    },
    'generic': {
      title: 'Become a DrawLab Host',
      subtitle: 'Unlock everything. Create bigger workshops. Gather more friends.',
    },
  };

  const { title, subtitle } = headlines[trigger] || headlines.generic;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header with gradient */}
        <div className="relative p-6 bg-gradient-to-br from-amber-500 to-rose-500 text-white">
          <button
            onClick={handleClose}
            disabled={purchasing}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-white/70 hover:text-white hover:bg-white/20 transition-colors disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>

          <p className="text-xs font-mono uppercase tracking-[0.2em] opacity-80 mb-2">
            Host Pass
          </p>
          <h2 className="text-2xl sm:text-3xl font-heading font-bold mb-2 pr-8">
            {title}
          </h2>
          <p className="text-sm opacity-90 leading-relaxed">
            {subtitle}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <p className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-3">
              What you unlock
            </p>
            <ul className="space-y-2.5">
              <Feature icon={Users} text="Host rooms with up to 8 players" />
              <Feature icon={Trophy} text="All 5 game modes — Challenge, Relay, Telephone, Corpse, Squiggle" />
              <Feature icon={BookOpen} text="Blind Relay variant for surprise reveals" />
              <Feature icon={Mic} text="Voice chat — talk while you draw" />
              <Feature icon={Sparkles} text="HD gallery + video export of your sessions" />
            </ul>
          </div>

          {/* Native: package selector */}
          {isNative && isSignedIn && packages.length > 0 && (
            <div className="space-y-2 pt-2">
              <p className="text-xs font-mono text-slate-400 uppercase tracking-wider mb-2">
                Choose your pass
              </p>
              {packages.map((pkg) => {
                const productId = pkg.product?.identifier;
                const tier =
                  productId === PRODUCTS.MONTHLY ? 'MONTHLY'
                  : productId === PRODUCTS.ANNUAL ? 'ANNUAL'
                  : productId === PRODUCTS.LIFETIME ? 'LIFETIME'
                  : null;
                if (!tier) return null;

                const isSelected = selectedTier === tier;
                const label =
                  tier === 'MONTHLY' ? 'Monthly'
                  : tier === 'ANNUAL' ? 'Annual'
                  : 'Lifetime';
                const sublabel =
                  tier === 'MONTHLY' ? 'Month-to-month'
                  : tier === 'ANNUAL' ? 'Best value — save vs monthly'
                  : 'One payment, yours forever';

                return (
                  <button
                    key={productId}
                    onClick={() => setSelectedTier(tier)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 transition-all text-left ${
                      isSelected
                        ? 'border-amber bg-amber/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div>
                      <p className="text-sm font-semibold text-charcoal font-display">{label}</p>
                      <p className="text-[11px] text-slate-400 font-display">{sublabel}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-charcoal font-mono">
                        {pkg.product?.priceString || ''}
                      </p>
                      {tier === 'ANNUAL' && (
                        <p className="text-[10px] text-amber font-mono uppercase">Popular</p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {/* The workshop message */}
          <div className="bg-charcoal text-canvas rounded-xl p-4 mt-4">
            <p className="text-xs font-mono text-amber-400 uppercase tracking-wider mb-1.5">
              The workshop tradition
            </p>
            <p className="text-sm font-display leading-relaxed text-canvas/80">
              Every Renaissance bottega had a master — the one who brought people together, set the brief,
              and made the workshop happen. Host Pass makes you that person.
            </p>
          </div>

          {errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-xs text-red-600 font-display leading-relaxed">{errorMessage}</p>
            </div>
          )}
        </div>

        {/* CTA footer */}
        <div className="p-5 border-t border-slate-100 bg-slate-50 shrink-0 space-y-2">
          {isNative && isSignedIn ? (
            <>
              <button
                onClick={handleNativePurchase}
                disabled={purchasing || packages.length === 0}
                className="w-full px-5 py-3 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {purchasing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Get Host Pass
                  </>
                )}
              </button>
              <button
                onClick={handleRestore}
                disabled={purchasing || restoring}
                className="w-full px-5 py-2 text-xs font-display text-slate-500 hover:text-charcoal transition-colors disabled:opacity-50"
              >
                {restoring ? 'Restoring…' : 'Restore purchases'}
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleWebUpgrade}
                className="w-full px-5 py-3 rounded-xl bg-amber text-white text-sm font-semibold font-display hover:bg-amber/90 transition-colors flex items-center justify-center gap-2"
              >
                <Sparkles className="w-4 h-4" />
                {isSignedIn ? 'Get Host Pass' : 'Sign in to get Host Pass'}
              </button>
              {isNative && !isSignedIn && (
                <p className="text-[11px] text-center font-mono text-slate-400">
                  Sign in with your DDD account to purchase
                </p>
              )}
              {!isNative && (
                <p className="text-[11px] text-center font-mono text-slate-400">
                  Host Pass purchases available in the iOS and Android apps
                </p>
              )}
            </>
          )}
          <button
            onClick={handleClose}
            disabled={purchasing}
            className="w-full px-5 py-2 text-xs font-display text-slate-400 hover:text-charcoal transition-colors disabled:opacity-50"
          >
            Maybe later
          </button>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <li className="flex items-start gap-2.5">
      <div className="w-5 h-5 rounded-full bg-amber/10 flex items-center justify-center shrink-0 mt-0.5">
        <Check className="w-3 h-3 text-amber" />
      </div>
      <span className="text-sm text-charcoal font-display leading-snug">{text}</span>
    </li>
  );
}
